import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './AdminDashboard.css';
import FailedTransactionsTable from '../components/admin/FailedTransactionsTable';
import CashFlowCharts from '../components/admin/CashFlowCharts';
import DashboardMetrics from '../components/admin/DashboardMetrics';
import PaymentSettings from '../components/admin/PaymentSettings';
import { adminService } from '../services/adminService';
import { authService } from '../services/authService';
import { AdminDashboardData } from '../types/admin.types';

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'failed' | 'cashflow' | 'settings'>('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const currentUser = authService.getCurrentUser();

  useEffect(() => {
    loadDashboardData();
  }, [refreshKey]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminService.getDashboardData();
      setDashboardData(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar datos del dashboard');
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin/login');
  };

  if (loading && !dashboardData) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error && !dashboardData) {
    return (
      <div className="admin-dashboard">
        <div className="error-container">
          <h3>⚠️ Error al cargar el dashboard</h3>
          <p>{error}</p>
          <button onClick={handleRefresh} className="btn-refresh">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Dashboard Administrativo</h1>
          <p className="header-subtitle">Sistema de Sistematización - Panel de Control</p>
        </div>
        <div className="header-actions">
          <div className="user-info">
            <span className="user-name">{currentUser?.FullName || 'Administrador'}</span>
            <span className="user-role">({currentUser?.Role || 'Admin'})</span>
          </div>
          <button
            onClick={handleRefresh}
            className="btn-refresh"
            disabled={loading}
          >
            {loading ? '🔄 Actualizando...' : '🔄 Actualizar'}
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className="btn-settings"
          >
            ⚙️ Configuración
          </button>
          <button
            onClick={handleLogout}
            className="btn-logout"
          >
            🚪 Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Resumen General
        </button>
        <button
          className={`tab-button ${activeTab === 'failed' ? 'active' : ''}`}
          onClick={() => setActiveTab('failed')}
        >
          ⚠️ Transacciones Fallidas
          {dashboardData?.FailedTransactions && dashboardData.FailedTransactions.length > 0 && (
            <span className="badge">{dashboardData.FailedTransactions.length}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'cashflow' ? 'active' : ''}`}
          onClick={() => setActiveTab('cashflow')}
        >
          💰 Flujo de Caja
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Configuración
        </button>
      </div>

      {/* Tab Content */}
      <div className="dashboard-content">
        {activeTab === 'overview' && dashboardData && (
          <>
            {/* Métricas Principales */}
            <DashboardMetrics metrics={dashboardData.Metrics} />

            {/* Últimas Transacciones */}
            {dashboardData.RecentTransactions && dashboardData.RecentTransactions.length > 0 && (
              <div className="section recent-transactions">
                <h2>Últimas Transacciones</h2>
                <div className="transactions-list">
                  <table className="transactions-table">
                    <thead>
                      <tr>
                        <th>Fecha</th>
                        <th>Placa</th>
                        <th>Cliente</th>
                        <th>Monto</th>
                        <th>Estado</th>
                        <th>Factura SAP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.RecentTransactions.map((tx) => (
                        <tr key={tx.TransactionId}>
                          <td>{new Date(tx.CreatedAt).toLocaleString('es-CO')}</td>
                          <td><strong>{tx.Placa}</strong></td>
                          <td>
                            {tx.CustomerName || tx.CustomerIdentification}
                            {tx.CustomerEmail && <div className="email">{tx.CustomerEmail}</div>}
                          </td>
                          <td className="amount">${tx.Amount.toLocaleString('es-CO')}</td>
                          <td>
                            <span className={`status-badge status-${tx.Status}`}>
                              {getStatusLabel(tx.Status)}
                            </span>
                          </td>
                          <td>
                            {tx.SapInvoiceDocNum ? (
                              <strong>#{tx.SapInvoiceDocNum}</strong>
                            ) : (
                              <span className="no-data">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Preview de Transacciones Fallidas */}
            {dashboardData.FailedTransactions && dashboardData.FailedTransactions.length > 0 && (
              <div className="section failed-preview">
                <h2>⚠️ Transacciones que Requieren Atención ({dashboardData.FailedTransactions.length})</h2>
                <p className="section-description">
                  Estas transacciones requieren intervención manual.
                  Ir a la pestaña "Transacciones Fallidas" para más detalles.
                </p>
                <button
                  className="btn-view-failed"
                  onClick={() => setActiveTab('failed')}
                >
                  Ver Todas las Transacciones Fallidas →
                </button>
              </div>
            )}
          </>
        )}

        {activeTab === 'failed' && dashboardData && (
          <FailedTransactionsTable onTransactionReprocessed={handleRefresh} />
        )}

        {activeTab === 'cashflow' && dashboardData && (
          <CashFlowCharts
            cashFlowToday={dashboardData.CashFlowToday}
            cashFlowWeek={dashboardData.CashFlowWeek}
            cashFlowMonth={dashboardData.CashFlowMonth}
          />
        )}

        {activeTab === 'settings' && (
          <PaymentSettings />
        )}
      </div>
    </div>
  );
};

// Helper function para obtener el label del estado
const getStatusLabel = (status: string): string => {
  const labels: { [key: string]: string } = {
    'pending': 'Pendiente',
    'processing': 'Procesando',
    'completed': 'Completado',
    'failed': 'Fallido',
    'cancelled': 'Cancelado'
  };
  return labels[status] || status;
};

export default AdminDashboard;
